import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SEO from "@/components/site/SEO";
import QuoteCTA from "@/components/site/QuoteCTA";
import { POSTS } from "@/lib/blog";

export default function Blog() {
  const sorted = [...POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "901 Lots Pavement Blog",
    url: "https://901lots.com/blog",
    blogPost: sorted.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `https://901lots.com/blog/${p.slug}`,
      author: { "@type": "Organization", name: "901 Lots" },
      image: p.image,
    })),
  };

  return (
    <div data-testid="blog-index-page">
      <SEO
        title="Parking Lot Maintenance Blog — Memphis & Mid-South"
        description="Practical pavement, striping, sealcoating, and ADA-compliance guides for Memphis and Mid-South business owners — written by the 901 Lots crew."
        path="/blog"
        jsonLd={blogSchema}
      />

      <PageHeader
        eyebrow="Blog"
        title="Pavement guides for Mid-South business owners."
        subtitle="Practical, no-jargon articles on striping, sealcoating, ADA compliance, and keeping your parking lot looking great year after year."
      />

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sorted.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(p.date).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {p.readTime}
                  </span>
                </div>
                <h2 className="mt-3 font-display font-semibold text-slate-900 text-xl leading-snug group-hover:text-blue-700 transition-colors">
                  {p.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                  {p.description}
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:gap-2 transition-all">
                  Read article <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
